import { useFormikContext } from 'formik';
import { ComponentPropsWithRef, MouseEvent, PropsWithChildren, ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUpdateEffect } from 'react-use';
import { Box, BoxProps, Flex } from 'reflexbox';
import styled, { css } from 'styled-components';

import { CheckmarkIcon } from '../../icons';
import { tokens } from '../../theme/tokens';
import { Skeleton } from '../Skeleton';
import { Spinner } from '../Spinner';
import {
	AIButton,
	Destructive as DestructiveBase,
	DisabledAI,
	DisabledDestructive,
	DisabledGhost,
	DisabledPrimary,
	DisabledSecondary,
	Ghost,
	InlineTextButton,
	Primary,
	Secondary,
} from './ButtonTypes';

const Icon = styled(Box)`
	flex-grow: 0;
	flex-shrink: 0;

	svg {
		display: block;
		width: 16px;
		height: 16px;
		margin: 0;
		color: inherit;

		path:not([fill='none']) {
			fill: currentColor;
		}
	}
`;

const Destructive = css<{ $loading?: boolean; success?: boolean }>`
	${DestructiveBase}

	${Icon} {
		color: ${(props) =>
			props.success
				? tokens.text.inverse
				: props.$loading
					? tokens.text.tertiary
					: tokens.error.icon};
	}

	&:active,
	&[data-preview='pressed'] {
		${Icon} {
			color: ${tokens.text.inverse};
		}
	}
`;

const WhiteTransparent = css`
	border-radius: ${tokens.radius.full}px;
	font-weight: ${tokens.typography.weights.medium};
	border: 1px solid transparent;
	color: ${({ theme }) => theme.colors.white};
	background: ${({ theme }) => theme.colors.WhiteTransparantMediumSoft};

	&:hover,
	&[data-preview='hover'] {
		background-color: ${({ theme }) => theme.colors.WhiteTransparantMedium};
	}

	&:active,
	&[data-preview='pressed'] {
		background-color: ${({ theme }) => theme.colors.WhiteTransparantMediumHard};
	}
`;

const ghostTypes = ['tertiary', 'plain', 'transparent', 'textIcon'] as const;
const secondaryTypes = ['secondary', 'alternative', 'card'] as const;

type ButtonTypes =
	| 'primary'
	| 'secondary'
	| 'tertiary'
	| 'alternative'
	| 'destructive'
	| 'plain'
	| 'transparent'
	| 'whiteTransparant'
	| 'card'
	| 'textIcon'
	| 'inlineTextButton'
	| 'ai';

interface IButtonProps {
	type: ButtonTypes;
	$loading?: boolean;
	success?: boolean;
	disabled?: boolean;
	small?: boolean;
	alignment?: 'start' | 'center' | 'end';
}

const ButtonStyle = css<IButtonProps>`
	position: relative;
	display: inline-flex;
	align-items: center;
	width: max-content;
	max-width: 100%;
	flex-grow: 0;
	flex-shrink: 0;
	gap: ${tokens.gap.inline}px;
	font-family: ${tokens.typography.fontFamily};
	font-weight: ${tokens.typography.weights.medium};
	font-size: ${tokens.typography.sizes.label}px;
	padding: 0 ${tokens.padding.control}px;
	border-radius: ${tokens.radius.full}px;
	cursor: pointer;
	user-select: none;
	transition: 0.1s ease-out all;
	line-height: 1;
	height: 33px;
	box-sizing: border-box;
	min-width: fit-content !important;

	${(props) =>
		props.alignment ? `justify-content: ${props.alignment};` : 'justify-content: center;'}
	${(props) => props.type === 'primary' && !props.disabled && Primary}
	${(props) =>
		secondaryTypes.includes(props.type as (typeof secondaryTypes)[number]) &&
		!props.disabled &&
		Secondary}
	${(props) =>
		ghostTypes.includes(props.type as (typeof ghostTypes)[number]) && !props.disabled && Ghost}
	${(props) => props.type === 'destructive' && !props.disabled && Destructive}
	${(props) => props.type === 'whiteTransparant' && !props.disabled && WhiteTransparent}
	${(props) => props.type === 'inlineTextButton' && !props.disabled && InlineTextButton}
	${(props) => props.type === 'ai' && !props.disabled && AIButton}
	${(props) => props.disabled && props.type === 'primary' && DisabledPrimary}
	${(props) =>
		props.disabled &&
		secondaryTypes.includes(props.type as (typeof secondaryTypes)[number]) &&
		DisabledSecondary}
	${(props) =>
		props.disabled &&
		ghostTypes.includes(props.type as (typeof ghostTypes)[number]) &&
		DisabledGhost}
	${(props) => props.disabled && props.type === 'destructive' && DisabledDestructive}
	${(props) => props.disabled && props.type === 'whiteTransparant' && DisabledGhost}
	${(props) => props.disabled && props.type === 'inlineTextButton' && DisabledGhost}
	${(props) => props.disabled && props.type === 'ai' && DisabledAI}
	${(props) => props.small && `padding: 0 ${tokens.padding.control}px;`}
	${(props) =>
		(props.$loading || props.success) &&
		css`
			&,
			&:hover,
			&:hover:active {
				box-shadow: none;
				padding-left: 34px;
				transform: none;
				cursor: default;
				border-color: ${props.$loading ? tokens.border.subtle : tokens.success.border};
				background: ${props.$loading ? tokens.surface.primary : tokens.success.icon};
				color: ${props.$loading ? tokens.text.tertiary : tokens.text.inverse};
			}
		`}
	svg {
		display: block;
		width: 16px;
		height: 16px;
		margin: 0;
		color: inherit;

		path:not([fill='none']) {
			fill: currentColor;
		}
	}
`;

const Container = styled(Flex)<IButtonProps>`
	${ButtonStyle}
`;

const SpinnerContainer = styled.div`
	position: absolute;
	top: 50%;
	margin-top: -8px;
	left: 11px;

	svg {
		display: block;
		width: 24px;
		height: 24px;
		margin: -4px;

		path {
			fill: ${tokens.text.inverse};
		}
	}
`;

const LinkOrHref = (
	props: PropsWithChildren<{
		to: string;
		target?: '_blank';
		navigationState?: object;
	}>,
) => {
	const { to, target, navigationState, children } = props;
	const isExternalLink = to.startsWith('http://') || to.startsWith('https://');

	const [pathname, params] = to.split('?') ?? [];
	const linkStyle = { display: 'inline-flex', maxWidth: '100%' } as const;

	if (isExternalLink) {
		return (
			<a
				href={to}
				target={target}
				rel={target ? 'noreferrer noopener' : undefined}
				style={linkStyle}
			>
				{children}
			</a>
		);
	} else {
		return (
			<Link
				to={{ pathname, search: params && `?${params}`, state: navigationState }}
				target={target}
				rel={target ? 'noreferrer noopener' : undefined}
				style={linkStyle}
				onClick={(event) => {
					event.stopPropagation();
				}}
			>
				{children}
			</Link>
		);
	}
};

export function Button(
	props: PropsWithChildren<
		BoxProps & {
			loading?: boolean;
			icon?: ReactNode;
			rightIcon?: ReactNode;
			title?: ReactNode;
			link?: string;
			type?: ButtonTypes;
			small?: boolean;
			disabled?: boolean;
			disableSuccess?: boolean;
			target?: '_blank';
			alignment?: 'start' | 'center' | 'end';
			onClick?: (event: MouseEvent) => Promise<void | unknown> | void;
			navigationState?: object;
		}
	>,
) {
	const { loading, title, disabled, type, onClick, target, navigationState, ...rest } = props;

	const [success, setSuccess] = useState(false);
	const [waiting, setWaiting] = useState(false);

	useUpdateEffect(() => {
		if (props.loading || props.disableSuccess) {
			return;
		}

		setSuccess(true);

		setTimeout(() => setSuccess(false), 2000);
	}, [props.loading]);

	const isLoading = loading || waiting;

	if (props.link) {
		return (
			<LinkOrHref to={props.link} target={target} navigationState={navigationState}>
				<Container
					$loading={isLoading}
					success={success}
					disabled={disabled}
					type={type ?? 'primary'}
					onClick={disabled || isLoading ? undefined : onClick}
					tabIndex={0}
					alignment={props.alignment}
					{...rest}
					display="inline-flex"
				>
					{loading ? (
						<SpinnerContainer>
							<Spinner />
						</SpinnerContainer>
					) : (
						success && (
							<SpinnerContainer>
								<CheckmarkIcon />
							</SpinnerContainer>
						)
					)}
					{props.icon && <Icon>{props.icon}</Icon>}
					{title}
					{props.rightIcon && <Icon>{props.rightIcon}</Icon>}
				</Container>
			</LinkOrHref>
		);
	}

	return (
		<Container
			$loading={isLoading}
			success={success}
			disabled={disabled}
			type={type ?? 'primary'}
			onClick={
				disabled || isLoading
					? undefined
					: async (event: MouseEvent) => {
							setWaiting(true);
							await onClick?.(event);
							setWaiting(false);
						}
			}
			tabIndex={0}
			{...rest}
			display="inline-flex"
		>
			{isLoading ? (
				<SpinnerContainer>
					<Spinner />
				</SpinnerContainer>
			) : (
				success && (
					<SpinnerContainer>
						<CheckmarkIcon />
					</SpinnerContainer>
				)
			)}
			{props.icon && <Icon>{props.icon}</Icon>}
			{title}
			{props.rightIcon && <Icon>{props.rightIcon}</Icon>}
		</Container>
	);
}

Button.Anchor = styled.a`
	display: block;
	${ButtonStyle}
`;

Button.Skeleton = styled(Skeleton.Box)<{ small?: boolean }>`
	${(props) =>
		props.small
			? `
			height: 35px;
			width: 110px;
		`
			: `
			height: 46px;
			width: 140px;
		`}
`;

Button.Styles = { Secondary, SecondaryNormal: Secondary };

function ButtonFormik(props: ComponentPropsWithRef<typeof Button>) {
	const formik = useFormikContext();

	return <Button {...props} loading={formik.isSubmitting} onClick={formik.submitForm} />;
}

Button.Formik = ButtonFormik;
